import { Injectable } from '@angular/core';
import { AppConstants, LMActionRequestSaveMode } from '@utils/index';
import { LMFileUploadService } from './file-upload/lm-fileupload.service';
import { SohoMessageService } from 'ids-enterprise-ng';
import { LMLoggerService } from './lm-logger.service';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LMContextService } from './lm-context.service';
import { LMFormSpecUtilService } from '@form/services';
import { LMCaptchaControlService } from '@field/captchacontrol/lm-captcha-control.service';

export interface LMBuildActionRequestForDataViewListArgs {
  dataViewLists: LMDataView[];
  actionSpec?: LMActionSpec;
  pfAction?: LMProcessFlowAction;
  validateOnly?: boolean;
  validatePanelName?: string;
  scheduleView?: LMDataView;
  pfActionRequestMode?: LMActionRequestSaveMode;
  validateKeysOnly?: boolean;
}

@Injectable()
export class LMActionService {

  constructor(
    private http: HttpClient,

    private messageService: SohoMessageService,

    private lmContextService: LMContextService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmFileUploadService: LMFileUploadService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmLoggerService: LMLoggerService,
    private lmCaptchaControlService: LMCaptchaControlService
  ) {}

  public isRecordLevelStandardAction(standardActionName: string) {
    switch (standardActionName) {
      case AppConstants.STANDARD_TOOLBAR_OPEN:
      case AppConstants.STANDARD_TOOLBAR_SAVECLOSE:
      case AppConstants.STANDARD_TOOLBAR_NAVIGATION:
      case AppConstants.STANDARD_TOOLBAR_ACTIONSMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON:
        return true;

      case AppConstants.STANDARD_SAVE:
      case AppConstants.STANDARD_TOOLBAR_SAVENEW:
      case AppConstants.STANDARD_TOOLBAR_EXPORTPDF:
      case AppConstants.STANDARD_TOOLBAR_EXPORTCSV:
      case AppConstants.STANDARD_TOOLBAR_SEARCH:
      case AppConstants.STANDARD_TOOLBAR_REFRESH:
      case AppConstants.STANDARD_TOOLBAR_VIEWACTION:
      case AppConstants.STANDARD_TOOLBAR_CHARTMENU_BUTTON:
      case AppConstants.STANDARD_TOOLBAR_HELPERLIST:
      case AppConstants.STANDARD_TOOLBAR_ALLACTIONSMENU_BUTTON:
        return false;
    }
    return false;
  }

  public isValidAction(
    actionSpec: LMActionSpec,
    actionsSpecArray: Array<LMActionSpec>,
    validActionIndexes: Array<number>,
    actionType?: LMActionSpecType,
    excludeParameterActions = true,
    isForm?: boolean
  ): boolean {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return false;
    }
    const validActionSpecs = this.getActions(actionsSpecArray, validActionIndexes, actionType, excludeParameterActions, isForm);
    return this._isValidAction(actionSpec, validActionSpecs);
  }

  private _isValidAction(actionSpec: LMActionSpec, validActionSpecs: Array<LMActionSpec>): boolean {
    for (let i = 0; i < validActionSpecs.length; i++) {
      if (validActionSpecs[ i ].type === actionSpec.type
        && validActionSpecs[ i ].name === actionSpec.name) {
        return true;
      }
    }

    return false;
  }

  public getActions(
    actions: Array<LMActionSpec>,
    actionIndexes: Array<number>,
    actionType: string = undefined,
    excludeParameterActions?: boolean,
    isForm?: boolean,
    excludeRequestAction?: boolean
  ): Array<LMActionSpec> {

    if (this.lmContextService.hasAsOfDateInPast()) {
      return [];
    }

    if (excludeParameterActions === undefined) {
      excludeParameterActions = false;
    }

    if (isForm === undefined) {
      isForm = false;
    }

    if (excludeRequestAction === undefined) {
      excludeRequestAction = false;
    }

    const validActions: Array<LMActionSpec> = [];

    if (!actions || !actionIndexes) {
      return validActions;
    }

    for (let i = 0; i < actionIndexes.length; i++) {
      const index = actionIndexes[ i ];
      const actionSpec = actions[ index ];

      if (!actionSpec) {
        continue;
      }

      if (actionType && actionSpec.type !== actionType) {
        continue;
      }

      if (excludeParameterActions
        && actionSpec.hasParameters) {
        continue;
      }

      if (isForm && actionSpec.type === AppConstants.ACTION_TYPE_UPDATE && !actionSpec.requestAction) {
        continue;
      }

      if (excludeRequestAction && actionSpec.type === AppConstants.ACTION_TYPE_UPDATE && actionSpec.requestAction) {
        continue;
      }

      validActions.push(actionSpec);
    }

    return validActions;
  }

  // todo akila, type pfAction please
  public buildActionRequestForDataView(
    dataView: LMDataView,
    actionSpec: LMActionSpec,
    pfAction?: LMProcessFlowAction,
    validateOnly?: boolean,
    validatePanelName?: string,
    scheduleView?: LMDataView,
    pfActionRequestMode?: LMActionRequestSaveMode,
    validateKeysOnly?: boolean,
    cubePeriodKey?: string,
    cubePeriodView?: string,
  ): LMActionRequest {

    if (scheduleView && Object.keys(scheduleView).length) {
      dataView.scheduleView = scheduleView;
    }

    const actionRequest: LMActionRequest = {
      dataView,
      confirmedMessageIds: [],
    };

    if (actionSpec) {
      actionRequest.actionSpec = actionSpec;
    }

    if (pfAction) {
      actionRequest.pfAction = pfAction;
    }

    if (validateOnly) {
      actionRequest.validateOnly = true;
      actionRequest.validatePanelName = validatePanelName ? validatePanelName : '';
    }

    if (validateKeysOnly) {
      actionRequest.validateKeysOnly = true;
      actionRequest.validatePanelName = validatePanelName ? validatePanelName : '';
    }

    if (pfActionRequestMode) {
      actionRequest.pfActionRequestMode = pfActionRequestMode;
    }

    if (cubePeriodKey) {
      actionRequest.cubePeriodKey = cubePeriodKey;
    }

    if (cubePeriodView) {
      actionRequest.cubePeriodKey = cubePeriodView;
    }
    return actionRequest;
  }

  public buildActionRequestForSaveList(dataViewLists: Array<LMDataView>, lmListSpec: LMListSpec, newSequenceField: string, cubePeriodKey?: string, cubePeriodView?: string): LMActionRequest[] {
    const result: LMActionRequest[] = [];

    if (newSequenceField) {
      this.lmFieldUtilService.sort(dataViewLists, newSequenceField, lmListSpec.busListSpec.displayFields);
    }

    for (let i = 0; i < dataViewLists.length; i++) {
      const targetDataView = lmListSpec.isTemplateList ? dataViewLists[ i ].dataView : dataViewLists[ i ];
      const busClassActions = lmListSpec.busClassSpec.actions;
      let actionSpec: LMActionSpec;

      if (lmListSpec.isTemplateList) {
        if (targetDataView.isAlreadyDefined) {
          actionSpec = this.getDefaultUpdateAction(busClassActions, targetDataView.actionIndexes, true, true);
        } else {
          actionSpec = this.getDefaultCreateAction(busClassActions, targetDataView.actionIndexes);
        }
      } else {
        actionSpec = this.getDefaultUpdateAction(busClassActions, targetDataView.actionIndexes, true, true);
      }

      if (!targetDataView.isNewRow && (actionSpec == null || actionSpec.hasParameters)) {
        // inline create pattern
        continue;
      }

      if (targetDataView.isNewRow) {
        actionSpec = this.getDefaultCreateAction(busClassActions, targetDataView.actionIndexes);
      }

      const dataViewObj = this.buildActionRequestForDataView(targetDataView, actionSpec, null, undefined, undefined, undefined,
        undefined, undefined, cubePeriodKey, cubePeriodView);
      result.push(dataViewObj);
    }

    return result;
  }

  public buildActionRequestForSaveForm(dataViewLists: Array<LMDataView>, lmFormSpec: LMFormSpec): LMActionRequest[] {
    const result: LMActionRequest[] = [];

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < dataViewLists.length; i++) {
      const targetDataView = lmFormSpec.isTemplateForm ? dataViewLists[ i ].dataView : dataViewLists[ i ];
      const busClassActions = lmFormSpec.busClassSpec.actions;
      let actionSpec: LMActionSpec;

      if (lmFormSpec.isTemplateForm) {
        if (targetDataView.isAlreadyDefined) {
          actionSpec = this.getDefaultUpdateAction(busClassActions, targetDataView.actionIndexes);
        } else {
          actionSpec = this.getDefaultCreateAction(busClassActions, targetDataView.actionIndexes);
        }
      } else {
        actionSpec = this.getDefaultUpdateAction(busClassActions, targetDataView.actionIndexes);
      }

      const dataViewObj = this.buildActionRequestForDataView(targetDataView, actionSpec, null);
      result.push(dataViewObj);
    }

    return result;
  }

  public buildActionRequestForDataViewList(args: LMBuildActionRequestForDataViewListArgs): LMActionRequest[] {
    const result: LMActionRequest[] = [];
    args.dataViewLists.forEach((dataViewList)=> {
      const actionRequest: LMActionRequest = this.buildActionRequestForDataView(dataViewList, args.actionSpec,
        args.pfAction, args.validateOnly, args.validatePanelName, args.scheduleView, args.pfActionRequestMode, args.validateKeysOnly);
      result.push(actionRequest);
    });

    return result;
  }

  /**
   * Returns an actionRequest array object that can be used to
   * call other this.functions that take actionRequest.
   */
  // todo type these arguments
  public buildActionRequestForDataViewSet(
    dataViewSet: LMDataViewSet,
    rows: any,
    actionSpec: LMActionSpec,
    pfAction: LMProcessFlowAction = undefined,
    isTemplate: any = false,
    scheduleDataView: LMDataView = undefined
  ): Array<any> {
    let i: number;
    let actionRequest: any;
    const actionRequestArr: Array<any> = [];
    let dataView: LMDataView;

    for (i = 0; i < rows.length; i++) {
      if (isTemplate) {
        dataView = dataViewSet.data[ rows[ i ].idx ].dataView;
      } else {
        dataView = dataViewSet.data[ rows[ i ].idx ];
      }

      // This code was added for LMCLIENT-11631 but it caused LMCLIENT-12454.
      // This line is not correct as the values should have already been encoded
      // and encoding an entire resourceId could cause and is causing major problems
      // as our escaping mechanism (curly brackets) is placed around the entire
      // resourceId and placed back in the dataViews resourceId.
      // ---
      // DO NOT CALL _encodeValue on anything, all encoding should be completed
      // by the server or in the KeyedResource constructor - Phillip 1/12/2016
      // dataView.resourceId = KeyedResource.prototype._encodeValue(dataView.resourceId);

      if (actionSpec !== undefined && actionSpec !== null) {
        // not sure about why all this is here... Phillip 7/10/17
        if (actionSpec.reasonCodeRequired) {
          dataView.fields._reason_code = dataView.fields._reason_code || null;
        }

        if (actionSpec.reasonCodeRequired || actionSpec.actionCommentRequired) {
          dataView.fields._action_comment = dataView.fields._action_comment || null;
        }

        if (actionSpec.effectiveDateRequired) {
          dataView.fields._effective_date = dataView.fields._effective_date || null;
        }
      }

      if (scheduleDataView !== undefined && scheduleDataView !== null) {
        dataView.scheduleView = scheduleDataView;
      }

      actionRequest = {
        dataView
      };

      if (actionSpec !== undefined && actionSpec !== null) {
        actionRequest.actionSpec = actionSpec;
      }

      if (pfAction !== undefined && pfAction !== null) {
        actionRequest.pfAction = pfAction;
      }

      actionRequestArr.push(actionRequest);
    }

    return actionRequestArr;
  }

  // --------------------------------------------------------------------------------
  // Run the action
  // --------------------------------------------------------------------------------
  public makeExecute(args: any): Observable<Response> {
    // let actionResource = args.actionResource;
    // let fromResource = args.fromResource;
    const actionRequestArray = args.actionRequestArray;
    const parameterView = args.parameterView;
    const isProcessFlowAction = args.isProcessFlowAction;
    const isCompareAction = args.isCompareAction;
    const templateForms = args.templateForms;
    const multiPartFields = args.multiPartFields;
    const templateMultiPartFields = args.templateMultiPartFields; // TODO lets look at always passing in array of MultiPartFields
    const url = args.url;
    const busFormSpec = args.busFormSpec;
    //  let callback = args.callback;

    // let busClassName = args.busClassName;
    // let actionName = isProcessFlowAction ? "_processflow" : isCompareAction ? "_executeCompare" : "_execute";

    let params: any = {};

    if (args.navigationContext) {
      params = Object.assign({}, this.lmGlobalUtilService.getNavContextKeys(args.navigationContext));
    }

    // params.executionType = isProcessFlowAction ? '_processflow' : isCompareAction ? '_executeCompare' : '_execute';
    // let url = this.urlUtilService.buildUrl({
    //   resource: actionResource,
    //   contextResource: fromResource,
    //   params: params
    // });

    const data: any = {
      actionRequestArray
    };

    if (args.parameterView) {
      data.paramView = parameterView;
    }

    if (args.isMatrixForm) {
      data.isMatrix = true;
    }

    const multiPartPost = false;
    let updateAction = false;

    let httpMethod = 'POST'; // TODO Philip this is not being used -- should it be removed
    if (data.actionRequestArray[ 0 ].actionSpec !== undefined) {
      if (data.actionRequestArray[ 0 ].actionSpec.type === AppConstants.ACTION_TYPE_CREATE
        || data.actionRequestArray[ 0 ].actionSpec.type === AppConstants.ACTION_TYPE_UPDATE
        || data.actionRequestArray[ 0 ].actionSpec.type === AppConstants.ACTION_TYPE_FUNCTION) {
        updateAction = true;
      }

      if (this.lmCaptchaControlService.getCaptchaValue() !== undefined) {
        data.actionRequestArray[0]['captchaValue'] = this.lmCaptchaControlService.getCaptchaValue();
      }

      // jetty doesn't handle PUT or DELETE, need to look into implementing in landmarks jetty handler.
      switch (data.actionRequestArray[ 0 ].actionSpec.type) {
        case AppConstants.ACTION_TYPE_CREATE:
        case AppConstants.ACTION_TYPE_IMPORT:
        case AppConstants.ACTION_TYPE_SET:
        case AppConstants.ACTION_TYPE_TEMPLATEFORM_SAVE:
        case AppConstants.ACTION_TYPE_AUDIT_OVERRIDE:
          httpMethod = 'POST';
          break;

        case AppConstants.ACTION_TYPE_UPDATE:
        case AppConstants.ACTION_TYPE_FUNCTION:
        case AppConstants.ACTION_TYPE_AUDIT_UPDATE:
        case AppConstants.ACTION_TYPE_CHANGE_EFFECTIVEDATE:
          httpMethod = 'PUT';
          break;

        case AppConstants.ACTION_TYPE_DELETE:
        case AppConstants.ACTION_TYPE_AUDIT_DELETE:
          httpMethod = 'DELETE';
          break;

        default:
          throw 'invalid action type: ' + data.actionRequestArray[ 0 ].actionSpec.type;
      }
    }

    const formName = busFormSpec ? busFormSpec.name : '';

    this.lmLoggerService.time({ id: 'makeExecuteAction' });

    if (multiPartFields) {
      const keys: Array<string> = Object.keys(multiPartFields);
      if (keys.length > 0) {
        return this.lmFileUploadService.makeFileRequest(formName, url, multiPartFields, data);
      }
    }

    if (templateMultiPartFields) {
      const hasMultiPartFields = false;
      for (let i = 0, len = templateMultiPartFields.length; i < len; i++) {
        const keys: Array<string> = Object.keys(templateMultiPartFields[i]);
        if (keys.length > 0) {
          const panelId = args.actionRequestArray[0].displayPanelId;
          return this.lmFileUploadService.makeTemplateFileRequest(panelId, formName, url, templateMultiPartFields, data);
        }
      }
    }

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return <Observable<Response>> this.http.post(url, JSON.stringify(data), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  private _handleError(error: Response) {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    // TODO: need to use LoggerService
    if(error && error.headers && error.headers.has('SSO_REDIRECTURL') && error.headers.get('SSO_REDIRECTURL').indexOf('action=LOGINREQD') >= 0) {
      return throwError({ status: AppConstants.STATUS_SSO_REDIRECTING, message: 'Redirecting' });
    } else {
      console.log(error);
      return throwError(error || 'Server error');
    }
  }

  /**
   * todo ppwatton move these functions that dont' have any dependencies to a new service -
   *  lm-spec-util.service.ts to be so it can be used w/o the problems of circular dependencies.
   *  NOTE: this function is also defined in lm-url-util.service.ts due to a circular dependency.
   */
  public getActionByName(actions: Array<LMActionSpec>, actionName: string): LMActionSpec {
    return actions.find(findActionSpec => findActionSpec.name === actionName);
  }

  public getActionIndexesByType(actions: Array<any>, actionType: string): Array<LMActionSpec> {
    return actions.filter(findActionSpec => findActionSpec.type === actionType);
  }

  public getCreateActions(actions: Array<LMActionSpec>, validActionIndexes: Array<number>): Array<LMActionSpec> {
    return this.getActions(actions, validActionIndexes, AppConstants.ACTION_TYPE_CREATE);
  }

  getDefaultCreateAction(actions: Array<LMActionSpec>, actionIndexes: Array<number>): LMActionSpec {
    const createActionSpecs: Array<LMActionSpec> = this.getActions(actions, actionIndexes, AppConstants.ACTION_TYPE_CREATE);
    if (createActionSpecs.length > 0) {
      return createActionSpecs[0];
    }
    return null;
  }

  public getDefaultUpdateAction(
    actions: Array<LMActionSpec>,
    actionIndexes: Array<number>,
    excludeParameterActions?: boolean,
    excludeRequestAction?: boolean
  ): LMActionSpec {

    if (excludeParameterActions === undefined) {
      excludeParameterActions = false;
    }

    if (excludeRequestAction === undefined) {
      excludeRequestAction = false;
    }

    const result: any = this.getActions(actions, actionIndexes, AppConstants.ACTION_TYPE_UPDATE, excludeParameterActions,
      false, excludeRequestAction);

    if (result.length > 0) {
      return result[ 0 ];
    }

    return null;
  }

  public isValidUpdateActionForForm(actionSpec: LMActionSpec, actionSpecArray: Array<LMActionSpec>, dataView: LMDataView) {
    const validActionSpecs: Array<any> = [];

    if (!actionSpecArray || !dataView.actionIndexes) {
      return false;
    }

    for (let i = 0, len = dataView.actionIndexes.length; i < len; i++) {
      const index = dataView.actionIndexes[ i ];

      if (actionSpecArray[ index ].type !== AppConstants.ACTION_TYPE_UPDATE) {
        continue;
      }

      if (actionSpecArray[ index ].requestAction) {
        continue;
      }

      validActionSpecs.push(actionSpecArray[ index ]);
    }

    return this._isValidAction(actionSpec, validActionSpecs);
  }

  public getImportActions(actions: Array<LMActionSpec>, validActionIndexes: Array<number>): Array<LMActionSpec> {
    return this.getActions(actions, validActionIndexes, AppConstants.ACTION_TYPE_IMPORT);
  }

  public getListDeleteAction(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet, rows: Array<number>): LMActionSpec {
    const deleteActions = this.getValidListDeleteActions(actions, dataViewSet, rows);
    if (deleteActions.length > 0) {
      return deleteActions[ 0 ];
    }

    return undefined;
  }

  /**
   *
   * @param actions
   * @param dataViewSet
   * @param rows
   * @returns
   */
  public getValidListDeleteActions(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet, rows: Array<number>): Array<LMActionSpec> {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return [];
    }

    return this.getValidActionsForList(actions, dataViewSet, rows, AppConstants.ACTION_TYPE_DELETE);
  }

  public hasStandardDelete(deleteActionSpec: Array<LMActionSpec>): boolean {
    return deleteActionSpec.some(action => action.type === 'DELETE' && !action.hasParameters && !action.hasActionForm);
  }

  public getFormDeleteAction(actions: Array<LMActionSpec>, dataView: LMDataView): LMActionSpec {
    const deleteActions = this.getValidFormDeleteActions(actions, dataView);
    if (deleteActions.length > 0) {
      return deleteActions[0];
    }
    return undefined;
  }

  /**
   *
   * @param actions
   * @param dataView
   * @returns
   */
  public getValidFormDeleteActions(actions: Array<LMActionSpec>, dataView: LMDataView): Array<LMActionSpec> {
    return this.getValidActionForForm(actions, dataView, AppConstants.ACTION_TYPE_DELETE);
  }

  /**
   * @deprecated why is this needed? Use getUpdateAction instead.
   * @param actions
   * @param dataViewSet
   * @param rows
   * @returns
   */
  public getValidListUpdateActions(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet, rows: Array<number>): Array<LMActionSpec> {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return undefined;
    }

    return this.getValidActionsForList(actions, dataViewSet, rows, AppConstants.ACTION_TYPE_UPDATE);
  }

  /**
   * @param actions
   * @param dataViewSet
   * @returns
   */
  public getValidListImportActions(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet): Array<LMActionSpec> {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return [];
    }
    return this.getValidActionForForm(actions, dataViewSet.header, AppConstants.ACTION_TYPE_IMPORT);
  }

  /**
   *
   * @param actions
   * @param dataView
   * @returns
   */
  public getValidFormUpdateActions(actions: Array<LMActionSpec>, dataView: LMDataView): Array<LMActionSpec> {
    return this.getValidActionForForm(actions, dataView, AppConstants.ACTION_TYPE_UPDATE);
  }

  /**
   *
   * @param actions
   * @param dataViewSet
   * @param rows
   * @returns
   */
  public getValidListInstanceActions(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet, rows: Array<any>): Array<LMActionSpec> {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return [];
    }

    return this.getValidActionsForList(actions, dataViewSet, rows, AppConstants.ACTION_TYPE_FUNCTION);
  }

  /**
   *
   * @param actions
   * @param dataView
   * @returns
   */
  public getValidFormInstanceActions(actions: Array<LMActionSpec>, dataView: LMDataView): Array<LMActionSpec> {
    return this.getValidActionForForm(actions, dataView, AppConstants.ACTION_TYPE_FUNCTION);
  }

  /**
   *
   * @param actions
   * @param dataViewSet
   * @param rows
   * @returns
   */
  public getValidListSetActions(actions: Array<LMActionSpec>, dataViewSet: LMDataViewSet): Array<LMActionSpec> {
    return this.getValidActionForForm(actions, dataViewSet?.header, AppConstants.ACTION_TYPE_SET);
  }

  /**
   *
   * @param actions
   * @param dataView
   * @returns
   */
  public getValidFormSetActions(actions: Array<LMActionSpec>, dataView: LMDataView): Array<LMActionSpec> {
    return this.getValidActionForForm(actions, dataView, AppConstants.ACTION_TYPE_SET);
  }

  /**
   *
   * @param actions
   * @param dataView
   * @param actionType
   * @returns
   */
  private getValidActionForForm(actions: Array<LMActionSpec>, dataView: LMDataView, actionType: string): Array<LMActionSpec> {
    let validActions: Array<any> = [];
    const actionIndexes = dataView?.actionIndexes;
    validActions = this.getActions(actions, actionIndexes, actionType);

    if (validActions.length === 0) {
      return [];
    }

    if (validActions.length > 0) {
      return validActions;
    }
  }

  public hasNonRestrictedAction(actions: Array<LMActionSpec>, actionType: LMActionSpecType): boolean {
    return actions.some(action => action.type === actionType);
  }

  /**
   *
   * @param actions
   * @param dataViewSet
   * @param rows
   * @param type
   * @returns
   */
  private getValidActionsForList(
    actions: Array<LMActionSpec>,
    dataViewSet: LMDataViewSet,
    rows: Array<number>,
    type: string
  ): Array<LMActionSpec> {
    let validActionsForType: Array<any> = [];
    let validRowActions: Array<any>;
    let isValidAction: boolean;
    const validActionForType: Array<any> = [];
    let actionIndexes: Array<any>;

    if (!dataViewSet) {
      this.lmLoggerService.warn('getValidAction is being passed an undefined dataViewSet.');
      return [];
    }

    if (!rows
      || rows.length === 0
      || (rows.length === 1 && rows[ 0 ] === -1)) {
      this.lmLoggerService.warn('getValidAction is being passed a dataViewSet with no selected records.');
      return [];
    }

    if (dataViewSet.data[ rows[ 0 ] ] === undefined) {
      this.lmLoggerService.warn('getValidAction is being passed a dataViewSet without data at the rows[0].idx.');
      return [];
    }

    const isTemplateList = dataViewSet.data[ rows[ 0 ] ].dataView ? true : false;
    const isTemplateDeleteActionType = AppConstants.ACTION_TYPE_DELETE === type && isTemplateList;
    // eslint-disable-next-line prefer-const
    actionIndexes = isTemplateList ? dataViewSet.data[ rows[ 0 ] ].dataView.actionIndexes : dataViewSet.data[ rows[ 0 ] ].actionIndexes;

    if (isTemplateDeleteActionType && !dataViewSet.data[ rows[ 0 ] ].dataView.isAlreadyDefined) {
      validActionsForType = [];
    } else {
      validActionsForType = this.getActions(actions, actionIndexes, type);
    }

    if (validActionsForType.length === 0) {
      return [];
    }

    if (rows.length === 1) {
      return validActionsForType;
    }

    // --------------------------------------------------------
    // run through all the valid actions of the first dataView
    // for each other dataView that is selected.
    // --------------------------------------------------------
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < validActionsForType.length; i++) {
      // Remove disable multiple instance selection actions from the valid actions list
      isValidAction = validActionsForType[ i ].disableMultipleInstanceSelection ? false : true;

      if (isValidAction) {
        for (let j = 1; j < rows.length; j++) {           // already checked the first dataView
          const dataView = dataViewSet.data[ rows[ j ] ];
          if (dataView !== undefined) {
            const targetDataView = isTemplateList ? dataView.dataView : dataView;
            if (isTemplateDeleteActionType && !targetDataView.isAlreadyDefined) {
              validRowActions = [];
            } else {
              validRowActions = this.getActions(actions, targetDataView.actionIndexes);
            }

            let k = 0;
            for (; k < validRowActions.length; k++) {
              if (validRowActions[ k ].type === validActionsForType[ i ].type
                && validRowActions[ k ].name === validActionsForType[ i ].name) {
                break;
              }
            }

            // --------------------------------------------------------
            // if this row doesn't have the compareAction then set
            // the isValidAction to false and break out of this loop.
            // --------------------------------------------------------
            if (k === validRowActions.length) {
              isValidAction = false;
              break;
            }
          }
        }

        // --------------------------------------------------------
        // if you get through all the actions and isValidAction
        // is still true then all rows should have that action available.
        // So go ahead and add it to the validActionsList.
        // --------------------------------------------------------
        if (isValidAction) {
          validActionForType.push(validActionsForType[ i ]);
        }
      }
    }

    return validActionForType;
  }
  public getValidProcessFlowActions(selectedRows: LMDataView[]): LMProcessFlowAction[] {
    if (this.lmContextService.hasAsOfDateInPast()) {
      return [];
    }
    if (selectedRows.length > 0) {
      if (selectedRows.length === 1) {
        return selectedRows[0].processFlowActions;
      }
      let allCommonPFActions = [];
      let commonPFActions = selectedRows[0].processFlowActions;
      for (let i = 1; i < selectedRows.length; i++) {
        const dataView = selectedRows[i];
        if (!dataView.processFlowActions || dataView.processFlowActions.length === 0) {
          return [];
        }

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let j = 0; j < commonPFActions.length; j++) {
          const pfAction = dataView.processFlowActions.find((a) =>
            (a.name === commonPFActions[j].name
              && a.processName === commonPFActions[j].processName
              && a.activityName === commonPFActions[j].activityName));
          if (pfAction) {
            allCommonPFActions.push(pfAction);
          }
        }

        if (allCommonPFActions.length === 0) {
          return [];
        } else {
          commonPFActions = [ ...allCommonPFActions ];
          allCommonPFActions = [];
        }
      }
      return commonPFActions;
    }
    return [];
  }

  // public methods
  public getTargetAction(params: {
    formSpec: LMFormSpec,
    dataView: LMDataView,
    actionName?: string
  }): LMActionSpec {
    const dataView: LMDataView = params.dataView;
    const formSpec: LMFormSpec = params.formSpec;
    let actionName: string = params.actionName;

    if (!formSpec.busClassSpec) {
      // page context forms do not have a business class
      // they are defined inline
      return null;
    }

    if (!actionName) {
      actionName = dataView.action;
    }

    let actionSpec: LMActionSpec = null;

    if (this.lmFormSpecUtilService.isBusinessTask(formSpec)) {
      actionSpec = formSpec.busClassSpec.actionSpec;
    } else {
      if (actionName && formSpec.busClassSpec.actions) {
        actionSpec = this.getActionByName(formSpec.busClassSpec.actions, actionName);
      }

      if (!actionSpec) {
        actionSpec = this.getDefaultUpdateAction(formSpec.busClassSpec.actions, dataView.actionIndexes, true);
      }
    }

    return actionSpec;
  }
}

export interface LMActionRequest {
  dataView?: LMDataView;
  actionSpec?: LMActionSpec;
  confirmedMessageIds?: string[];
  pfAction?: LMProcessFlowAction;
  validateOnly?: boolean;
  validatePanelName?: string;
  unmodifiedConfirmation?: boolean;
  // for template form save
  templateDataViewSet?: LMDataViewSet,
  subPanelResourceId?: string;
  displayWhenIndex?: number;
  displayPanelId?: string;
  // Create action request (save mode)
  pfActionRequestMode?: LMActionRequestSaveMode;

  // for job parameters
  validateKeysOnly?: boolean;
  // For cube save
  cubePeriodKey?: string;
  cubePeriodView?: string;
}
